import React, { useState } from 'react';
import { Paper, Typography, Button, Box, Grid, Divider } from '@mui/material';
import PoTable from './PoTable';
import { useDispatch, useSelector } from 'react-redux';
import { formateDate } from '../../../../Website/utils/utils';
import { useNavigate } from 'react-router-dom';
import { addToScheduleThunk } from '../../../../Website/redux/ScheduleSlice';
import { setScheduleOrderRefresh } from '../../../../Website/redux/scheduleOrderSlice';
import api from '../../../../Website/utils/api';
import axios from 'axios';
import { toast } from 'react-toastify';
import OrderProgress from '../orderscomp/OrderProgress';

const PODetailsComp = ({ id }) => {
    const { loading, orderDetails } = useSelector(state => state.scheduleOrder);
    const navigate = useNavigate();
    const [selectedItems, setSelectedItems] = useState([]);
    const dispatch = useDispatch();
    const [reorderloading, setreorderloading] = useState(false);
    const [poUploading, setPoUploading] = useState(false);
    const [poProgress, setPoProgress] = useState(0);

    // Upload the file to storage, then save its URL on this order so the admin can see it
    const handlePOUpload = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        if (file.size > 25 * 1024 * 1024) {
            toast.error('File must be under 25MB');
            return;
        }
        setPoUploading(true);
        setPoProgress(0);
        try {
            // Plain file name keeps the stored URL clean (no spaces or special characters)
            const safeName = file.name.replace(/[^a-zA-Z0-9._-]+/g, '_');
            const { data, status } = await api.getPutSignedUrl({
                key: `${Date.now()}_${safeName}`,
                fileType: file.type,
                fileName: safeName,
            });
            if (status !== 200) throw new Error('Could not get upload URL');
            await axios.put(data?.data?.preSigned, file, {
                headers: { 'Content-Type': file.type },
                onUploadProgress: (e) => {
                    if (e.total) setPoProgress(Math.round((e.loaded * 100) / e.total));
                },
            });
            await api.uploadSchedulePurchaseOrder({ id: orderDetails?._id || id, purchaseOrderUri: data?.data?.url });
            toast.success('PO uploaded successfully');
            dispatch(setScheduleOrderRefresh());
        } catch (err) {
            console.log(err);
            toast.error(err?.response?.data?.message || 'Error uploading PO');
        } finally {
            setPoUploading(false);
        }
    };

    const handleReorder = async () => {
        const data = orderDetails?.products?.map((i) => ({
            productId: i?.productId?._id,
            totalQty: parseInt(i?.totalQty, 10),
            quantity: parseInt(i?.quantity, 10),
        }));
        setreorderloading(true);
        const status = await dispatch(addToScheduleThunk(data));
        setreorderloading(false);

        if (status?.payload === 200) {
            navigate('/scheduleCart');
        }
    };

    // Shipment progress across the whole order (drives the banner and the per-line status)
    const products = orderDetails?.products || [];
    const totalQty = products.reduce((sum, p) => sum + (Number(p?.totalQty) || 0), 0);
    const pendingQty = products.reduce((sum, p) => sum + (Number(p?.pendingOrderCount) || 0), 0);
    const shippedQty = Math.max(0, totalQty - pendingQty);
    const linesShipped = products.filter((p) => (Number(p?.pendingOrderCount) || 0) < (Number(p?.totalQty) || 0)).length;
    const dispatches = [...(orderDetails?.dispatches || [])].reverse();
    const shipment = totalQty > 0 && pendingQty === 0
        ? { tone: '#059669', bg: '#ECFDF5', border: '#A7F3D0', title: 'Shipped', text: 'All products on this order have been dispatched. Invoice and tracking information is available below.' }
        : dispatches.length > 0
            ? { tone: '#B45309', bg: '#FFFBEB', border: '#FDE68A', title: 'Partially shipped', text: `${linesShipped} of ${products.length} line item(s) dispatched so far (${shippedQty.toLocaleString('en-IN')} of ${totalQty.toLocaleString('en-IN')} pcs). The rest is pending.` }
            : orderDetails?.status === 'CANCELLED'
                ? { tone: '#B91C1C', bg: '#FEF2F2', border: '#FECACA', title: 'Cancelled', text: 'This order has been cancelled.' }
                : { tone: '#512DA8', bg: '#F4F3FF', border: '#DDD6FE', title: orderDetails?.status === 'NEW' ? 'Order received' : 'Processing', text: 'No shipments yet. Invoices and tracking details will appear here as soon as items are dispatched.' };

    // GST shown on the summary. Older schedules were saved with tax 0, so when nothing is
    // stored the GST is derived from the lines (their gstPercent, default 18%).
    const DEFAULT_GST = 18;
    const subTotalAmt = Number(orderDetails?.priceBreakup?.subTotal) || 0;
    const lineRates = [...new Set(products.map((p) => (Number.isFinite(Number(p?.gstPercent)) ? Number(p.gstPercent) : DEFAULT_GST)))];
    const gstRate = lineRates.length === 1 ? lineRates[0] : null;
    const otherAmt = Number(orderDetails?.priceBreakup?.otherCharges) || 0;
    // GST amount: stored tax, else the rate applied to the subtotal (older schedules were saved with tax 0)
    const storedTax = Number(orderDetails?.priceBreakup?.tax) || 0;
    const gstAmt = storedTax > 0 ? storedTax : (subTotalAmt * (gstRate ?? DEFAULT_GST)) / 100;
    const grandTotal = subTotalAmt + gstAmt + otherAmt;

    const copyText = (value) => {
        if (!value) return;
        navigator.clipboard?.writeText(String(value));
        toast.success('Copied');
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px 0' }}>
                <img src="/assets/__Iphone-spinner-1.gif" alt="loading" style={{ width: 35, height: 35 }} />
            </Box>
        );
    }

    return (
        <Paper sx={{ width: '100%', borderRadius: '16px', boxShadow: '0 4px 24px rgba(0,0,0,0.06)', overflow: 'hidden', border: '1px solid #f1f5f9' }}>
            {/* Header Bar */}
            <Box sx={{ padding: '24px 30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fafafa', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap', gap: '16px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: "20px", color: '#0f172a', fontWeight: '700', fontFamily: 'inherit' }}>
                        Order Details
                    </Typography>
                    <Typography sx={{ color: '#cbd5e1' }}>•</Typography>
                    <Typography sx={{ fontSize: '13px', color: '#64748b', fontWeight: '500' }}>
                        {formateDate(orderDetails?.createdAt)}
                    </Typography>
                    {orderDetails?.userId?.fullName && (
                        <>
                            <Typography sx={{ color: '#cbd5e1' }}>•</Typography>
                            <span style={{
                                backgroundColor: 'rgba(81, 45, 168, 0.08)',
                                color: '#512DA8',
                                padding: '4px 10px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: '600'
                            }}>
                                Created by {orderDetails?.userId?.fullName}
                            </span>
                        </>
                    )}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    {orderDetails?.purchaseOrderUri && (
                        <Button
                            component="a"
                            href={orderDetails.purchaseOrderUri}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                            sx={{
                                color: '#512DA8',
                                textTransform: 'none',
                                fontSize: '13px',
                                fontWeight: '600',
                                padding: '6px 10px',
                                textDecoration: 'underline',
                            }}
                        >
                            View PO
                        </Button>
                    )}
                    <Button
                        component="label"
                        variant="contained"
                        size="small"
                        disabled={poUploading}
                        sx={{
                            background: '#512DA8',
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontSize: '13px',
                            fontWeight: '600',
                            padding: '6px 14px',
                            boxShadow: 'none',
                            '&:hover': { background: '#4527a0', boxShadow: 'none' }
                        }}
                    >
                        {poUploading ? `Uploading... ${poProgress}%` : orderDetails?.purchaseOrderUri ? 'Replace PO' : 'Upload PO'}
                        <input
                            hidden
                            type="file"
                            accept=".pdf,.xls,.xlsx,.csv,.doc,.docx,.png,.jpg,.jpeg"
                            onChange={handlePOUpload}
                        />
                    </Button>
                    <Button
                        onClick={() => navigate(-1)}
                        variant="outlined"
                        size="small"
                        sx={{
                            color: '#512DA8',
                            borderColor: '#512DA8',
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontSize: '13px',
                            fontWeight: '600',
                            padding: '6px 14px',
                            '&:hover': {
                                background: 'rgba(81, 45, 168, 0.04)',
                                borderColor: '#4527a0'
                            }
                        }}
                    >
                        Back to List
                    </Button>
                </Box>
            </Box>

            <Box sx={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {/* Shipment status, like a marketplace order page */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '12px 16px', borderRadius: '10px', background: shipment.bg, border: `1px solid ${shipment.border}` }}>
                    <Box sx={{ width: 34, height: 34, borderRadius: '8px', background: '#fff', border: `1px solid ${shipment.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={shipment.tone} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="1" y="3" width="15" height="13" rx="2" /><polygon points="16 8 20 8 23 11 23 16 16 16 16 8" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" />
                        </svg>
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: '14px', fontWeight: '700', color: shipment.tone }}>{shipment.title}</Typography>
                        <Typography sx={{ fontSize: '13px', color: '#475569', marginTop: '2px' }}>{shipment.text}</Typography>
                    </Box>
                </Box>

                {/* Shipments: one row per dispatch, with invoice, courier and tracking */}
                {dispatches.length > 0 && <Box sx={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden' }}>
                    <Box sx={{ padding: '14px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>Shipments</Typography>
                        <Typography sx={{ fontSize: '12px', color: '#94a3b8' }}>{dispatches.length} shipment{dispatches.length === 1 ? '' : 's'}</Typography>
                    </Box>
                    {(
                        <Box sx={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
                                <thead>
                                    <tr style={{ background: '#f8fafc' }}>
                                        {['Ship date', 'Documents', 'Ship method', 'Items', 'Status', 'Amount'].map((h, i) => (
                                            <th key={h} style={{ padding: '10px 16px', fontSize: '11.5px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#64748b', textAlign: i >= 4 ? 'right' : 'left', whiteSpace: 'nowrap' }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {dispatches.map((d, idx) => {
                                        const itemCount = (d?.items || []).reduce((sum, it) => sum + (Number(it?.quantity) || 0), 0);
                                        return (
                                            <tr key={d?._id || idx} style={{ borderTop: '1px solid #f1f5f9', verticalAlign: 'top' }}>
                                                <td style={{ padding: '14px 16px', fontSize: '13.5px', color: '#0f172a', fontWeight: 600, whiteSpace: 'nowrap' }}>{formateDate(d?.dispatchedAt)}</td>
                                                <td style={{ padding: '14px 16px', fontSize: '13.5px' }}>
                                                    {d?.invoiceUri ? (
                                                        <a href={d.invoiceUri} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#512DA8', fontWeight: 600, textDecoration: 'none' }}>
                                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                                                            Invoice # {d?.invoiceNumber || '-'}
                                                        </a>
                                                    ) : (
                                                        <span style={{ color: '#0f172a', fontWeight: 600 }}>Invoice # {d?.invoiceNumber || '-'}</span>
                                                    )}
                                                    {d?.remarks && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{d.remarks}</div>}
                                                </td>
                                                <td style={{ padding: '14px 16px', fontSize: '13.5px', color: '#0f172a' }}>
                                                    {d?.courierName || d?.trackingNumber ? (
                                                        <>
                                                            <div style={{ fontWeight: 600 }}>{d?.courierName || 'Courier'}</div>
                                                            {d?.trackingNumber && (
                                                                <div style={{ fontSize: 12.5, color: '#512DA8', marginTop: 2, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                                                    {d.trackingNumber}
                                                                    <button type="button" onClick={() => copyText(d.trackingNumber)} title="Copy tracking number" style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, color: '#94a3b8', lineHeight: 0 }}>
                                                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </>
                                                    ) : <span style={{ color: '#94a3b8' }}>To be updated</span>}
                                                </td>
                                                <td style={{ padding: '14px 16px', fontSize: '13px', color: '#475569' }}>
                                                    <div style={{ fontWeight: 600, color: '#0f172a' }}>{(d?.items || []).length} line{(d?.items || []).length === 1 ? '' : 's'} · {itemCount.toLocaleString('en-IN')} pcs</div>
                                                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{(d?.items || []).map((it) => `${it?.productNumber || 'Part'} × ${it?.quantity}`).join(', ')}</div>
                                                </td>
                                                <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                                                    <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 999, fontSize: 11.5, fontWeight: 700, background: '#ECFDF5', color: '#047857', border: '1px solid #A7F3D0', whiteSpace: 'nowrap' }}>Dispatched</span>
                                                </td>
                                                <td style={{ padding: '14px 16px', fontSize: '13.5px', fontWeight: 700, color: '#0f172a', textAlign: 'right', whiteSpace: 'nowrap' }}>₹{Number(d?.subTotal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </Box>
                    )}
                </Box>}

                {/* Order facts as tiles: id, PO, payment, shipment progress */}
                {(selectedItems?.length > 0 || orderDetails?.status === "COMPLETED") && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                        {selectedItems?.length > 0 && (
                            <Button onClick={() => navigate(`/reshedule/${id}`, { state: selectedItems })} variant="contained" size="small"
                                sx={{ background: '#512DA8', borderRadius: '8px', textTransform: 'none', fontSize: '13px', fontWeight: '600', padding: '6px 16px', boxShadow: 'none', '&:hover': { background: '#4527a0', boxShadow: 'none' } }}>
                                Reschedule ({selectedItems.length})
                            </Button>
                        )}
                        {orderDetails?.status === "COMPLETED" && (
                            <Button onClick={handleReorder} disabled={reorderloading} variant="contained" size="small"
                                sx={{ background: '#512DA8', borderRadius: '8px', textTransform: 'none', fontSize: '13px', fontWeight: '600', padding: '6px 16px', boxShadow: 'none', '&:hover': { background: '#4527a0', boxShadow: 'none' } }}>
                                {reorderloading ? "Please wait..." : "Reorder"}
                            </Button>
                        )}
                    </Box>
                )}
                <Grid container spacing={2}>
                    {[
                        {
                            label: 'Order ID', value: `#${orderDetails?._id ?? ''}`, sub: `Placed ${formateDate(orderDetails?.createdAt)}`,
                            icon: <path d="M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />,
                        },
                        {
                            label: 'PO number', value: orderDetails?.poNumber || 'Not set', muted: !orderDetails?.poNumber,
                            sub: orderDetails?.period > 0 ? `${orderDetails.period} month schedule` : (orderDetails?.scheduleStartDate ? `From ${formateDate(orderDetails.scheduleStartDate)}` : 'Schedule order'),
                            icon: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="8" y1="13" x2="16" y2="13" /><line x1="8" y1="17" x2="16" y2="17" /></>,
                        },
                        {
                            label: 'Payment', value: 'Offline', sub: orderDetails?.deliveredDate ? `Last delivery ${formateDate(orderDetails.deliveredDate)}` : 'Invoiced per shipment',
                            icon: <><rect x="1" y="4" width="22" height="16" rx="2" /><line x1="1" y1="10" x2="23" y2="10" /></>,
                        },
                        {
                            label: 'Shipped', value: `${shippedQty.toLocaleString('en-IN')} / ${totalQty.toLocaleString('en-IN')} pcs`,
                            sub: `${linesShipped} of ${products.length} lines · ${dispatches.length} shipment${dispatches.length === 1 ? '' : 's'}`,
                            progress: totalQty > 0 ? Math.round((shippedQty / totalQty) * 100) : 0,
                            icon: <><rect x="1" y="3" width="15" height="13" rx="2" /><polygon points="16 8 20 8 23 11 23 16 16 16 16 8" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" /></>,
                        },
                    ].map((t) => (
                        <Grid item xs={12} sm={6} md={3} key={t.label}>
                            <Box sx={{ height: '100%', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '14px 16px', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                                <Box sx={{ width: 36, height: 36, borderRadius: '10px', background: 'rgba(81, 45, 168, 0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#512DA8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{t.icon}</svg>
                                </Box>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography sx={{ fontSize: '11px', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t.label}</Typography>
                                    <Typography sx={{ fontSize: '15px', fontWeight: 700, color: t.muted ? '#94a3b8' : '#0f172a', marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.value}</Typography>
                                    {t.progress !== undefined && (
                                        <Box sx={{ height: 6, borderRadius: 999, background: '#EEF2F7', marginTop: '8px', overflow: 'hidden' }}>
                                            <Box sx={{ width: `${t.progress}%`, height: '100%', background: t.progress >= 100 ? '#10b981' : '#512DA8', borderRadius: 999, transition: 'width .3s' }} />
                                        </Box>
                                    )}
                                    <Typography sx={{ fontSize: '12px', color: '#64748b', marginTop: t.progress !== undefined ? '6px' : '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.sub}</Typography>
                                </Box>
                            </Box>
                        </Grid>
                    ))}
                </Grid>

                {/* Shipping & Order Summary Grid */}
                <Grid container spacing={2} alignItems="stretch">
                    <Grid item xs={12} md={7}>
                        <Paper sx={{ padding: '16px 20px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
                            <Typography sx={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                                Shipping Address
                            </Typography>
                            {(orderDetails?.userId?.companyName || orderDetails?.userId?.fullName) && (
                                <Typography sx={{ fontSize: '13px', fontWeight: '600', color: '#512DA8', marginBottom: '2px' }}>
                                    {orderDetails?.userId?.companyName || orderDetails?.userId?.fullName}
                                </Typography>
                            )}
                            <Typography sx={{ fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>
                                {orderDetails?.shippingAddress?.factoryUnits || 'Factory / Unit Details'}
                            </Typography>
                            <Typography sx={{ fontSize: '13.5px', color: '#475569', marginTop: '6px', lineHeight: '1.5' }}>
                                {[orderDetails?.shippingAddress?.streetAddress, orderDetails?.shippingAddress?.state, orderDetails?.shippingAddress?.country].filter(Boolean).join(', ')}
                            </Typography>

                            <Box sx={{ display: 'flex', gap: '24px', marginTop: 'auto', paddingTop: '12px', borderTop: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
                                {orderDetails?.userId?.email && (
                                    <Box>
                                        <Typography sx={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600' }}>EMAIL</Typography>
                                        <Typography sx={{ fontSize: '13px', color: '#0f172a', fontWeight: '500', marginTop: '2px' }}>
                                            {orderDetails.userId.email}
                                        </Typography>
                                    </Box>
                                )}
                                {orderDetails?.userId?.phone && (
                                    <Box>
                                        <Typography sx={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600' }}>PHONE</Typography>
                                        <Typography sx={{ fontSize: '13px', color: '#0f172a', fontWeight: '500', marginTop: '2px' }}>
                                            {orderDetails.userId.phone}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={5}>
                        <Paper sx={{ padding: '16px 20px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', background: '#fafafa', height: '100%', boxSizing: 'border-box' }}>
                            <Typography sx={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                                Order summary
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                                    <Typography sx={{ color: '#64748b' }}>Subtotal</Typography>
                                    <Typography sx={{ fontWeight: '600', color: '#0f172a' }}>₹{Number(orderDetails?.priceBreakup?.subTotal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                                    <Typography sx={{ color: '#64748b' }}>Tax{gstRate !== null ? ` (${gstRate}%)` : ''}</Typography>
                                    <Typography sx={{ fontWeight: '600', color: '#0f172a' }}>₹{gstAmt.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                                    <Typography sx={{ color: '#64748b' }}>Shipping</Typography>
                                    <Typography sx={{ fontWeight: '600', color: '#10b981' }}>Free</Typography>
                                </Box>
                                <Divider sx={{ my: '4px' }} />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '15px' }}>
                                    <Typography sx={{ fontWeight: '700', color: '#0f172a' }}>Total</Typography>
                                    <Typography sx={{ fontWeight: '800', color: '#512DA8', fontSize: '16px' }}>₹{grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                                </Box>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>

                {/* Order tracker */}
                <OrderProgress status={orderDetails?.status} type="schedule" />

                {/* Products Table */}
                <Box sx={{ marginTop: '10px' }}>
                    <PoTable
                        data={orderDetails?.products}
                        orderDetails={orderDetails}
                        status={orderDetails?.status}
                        selectedItems={selectedItems}
                        setSelectedItems={setSelectedItems}
                    />
                </Box>
            </Box>
        </Paper>
    );
};

export default PODetailsComp;
